// Чистая логика экранов журнала здоровья: пометки, серии для графиков, подсказки. Без React.

import { ApiError } from '@/api/client'
import type { ChartSeries } from '@/components/charts/TimeSeriesChart'

import type { BloodPressure, Lab, NutritionPoint } from './api'

export const BP_HIGH_SYSTOLIC = 140
export const BP_HIGH_DIASTOLIC = 90

export function isPressureHigh(systolic: number, diastolic: number): boolean {
  return systolic >= BP_HIGH_SYSTOLIC || diastolic >= BP_HIGH_DIASTOLIC
}

/** Две линии давления от старых замеров к новым. */
export function pressureSeries(items: BloodPressure[]): ChartSeries[] {
  const sorted = [...items].sort((a, b) => a.measured_at.localeCompare(b.measured_at))
  return [
    {
      key: 'systolic',
      label: 'Верхнее',
      unit: 'мм рт. ст.',
      axis: 'left',
      digits: 0,
      points: sorted.map((p) => ({ x: p.measured_at, y: p.systolic })),
    },
    {
      key: 'diastolic',
      label: 'Нижнее',
      unit: 'мм рт. ст.',
      axis: 'left',
      digits: 0,
      points: sorted.map((p) => ({ x: p.measured_at, y: p.diastolic })),
    },
  ]
}

/** График одного показателя анализа между сдачами. */
export function labSeries(history: Lab[]): ChartSeries[] {
  const sorted = [...history].sort((a, b) => a.taken_on.localeCompare(b.taken_on))
  return [
    {
      key: 'value',
      label: sorted[0]?.marker ?? 'Значение',
      unit: sorted[0]?.unit || undefined,
      digits: 2,
      points: sorted.map((l) => ({ x: l.taken_on, y: l.value })),
    },
  ]
}

function present(points: NutritionPoint[], pick: (p: NutritionPoint) => number | null) {
  // Дни без данных пропускаются, а не рисуются нулём.
  return points.flatMap((p) => {
    const y = pick(p)
    return y === null ? [] : [{ x: p.day, y }]
  })
}

/** Калории: съедено против расхода (расход — из Garmin, когда он есть). */
export function caloriesSeries(points: NutritionPoint[]): ChartSeries[] {
  return [
    { key: 'eaten', label: 'Съедено', unit: 'ккал', axis: 'left', digits: 0, points: present(points, (p) => p.kcal) },
    {
      key: 'burned',
      label: 'Расход',
      unit: 'ккал',
      axis: 'left',
      digits: 0,
      points: present(points, (p) => p.burned_kcal),
    },
  ]
}

export function proteinSeries(points: NutritionPoint[]): ChartSeries[] {
  return [{ key: 'protein', label: 'Белок', unit: 'г', digits: 0, points: present(points, (p) => p.protein_g) }]
}

/** Записанные дни питания, новые сверху. */
export function nutritionEntries(points: NutritionPoint[]): NutritionPoint[] {
  return points.filter((p) => p.id !== null).sort((a, b) => b.day.localeCompare(a.day))
}

export function adherencePercent(share: number): number {
  return Math.round(Math.min(1, Math.max(0, share)) * 100)
}

/** «30–400», «до 5,9», «от 30»; нормы нет — пустая строка. */
export function referenceText(low: number | null, high: number | null, fmt: (n: number) => string): string {
  if (low !== null && high !== null) return `${fmt(low)}–${fmt(high)}`
  if (high !== null) return `до ${fmt(high)}`
  if (low !== null) return `от ${fmt(low)}`
  return ''
}

/** Сообщение ошибки API для поля формы, если ошибка относится к одному из перечисленных полей. */
export function fieldError(error: unknown, ...fields: string[]): string | null {
  if (!(error instanceof ApiError) || !error.field) return null
  return fields.includes(error.field) ? error.message : null
}

/** Ошибка без привязки к перечисленным полям — для общей строки под формой. */
export function otherError(error: unknown, ...fields: string[]): unknown {
  if (!error) return null
  if (error instanceof ApiError && error.field && fields.includes(error.field)) return null
  return error
}

/** Значение datetime-local → ISO для API; пустое — null (сервер возьмёт «сейчас»). */
export function localToIso(value: string): string | null {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}
